import { useCallback, useEffect, useState } from "react";

import { useAdminFetch } from "../../admin/hooks/useAdminFetch";

const safeJsonParse = async (response, fallback) => {
  try {
    return JSON.parse(await response.text());
  } catch {
    return fallback;
  }
};

const formatApiError = (payload, fallback) => {
  const detail = payload?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail
      .map((entry) => {
        if (typeof entry === "string") return entry;
        if (entry && typeof entry === "object") {
          const path = Array.isArray(entry.loc) ? entry.loc.slice(1).join(".") : "";
          const message = entry.msg || "";
          return path && message ? `${path}: ${message}` : message || path || "";
        }
        return "";
      })
      .filter(Boolean)
      .join("; ") || fallback;
  }
  return fallback;
};

export function useStakeholderContactHistory(item) {
  const adminFetch = useAdminFetch();
  const [entries, setEntries] = useState([]);
  const [contactState, setContactState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingAction, setSavingAction] = useState("");
  const [savingEntryId, setSavingEntryId] = useState("");
  const [deletingEntryId, setDeletingEntryId] = useState("");
  const [savingFollowUp, setSavingFollowUp] = useState(false);
  const [error, setError] = useState("");

  const loadEntries = useCallback(async () => {
    if (!item?.id) {
      setEntries([]);
      setContactState(null);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await adminFetch(`/admin/stakeholder/contacts/${encodeURIComponent(item.id)}`);
      const payload = await safeJsonParse(response, { entries: [] });
      if (!response.ok) {
        throw new Error(formatApiError(payload, "Failed to load contact history"));
      }
      setEntries(Array.isArray(payload.entries) ? payload.entries : []);
      setContactState(payload.state || null);
    } catch (fetchError) {
      setError(fetchError.message || "Failed to load contact history");
    } finally {
      setLoading(false);
    }
  }, [adminFetch, item?.id]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const logAction = useCallback(
    async ({ actionType, note = "" }) => {
      if (!item?.id) return false;

      setSavingAction(actionType);
      setError("");
      try {
        const response = await adminFetch(`/admin/stakeholder/contacts/${encodeURIComponent(item.id)}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            lane: item.lane,
            title_at_time: item.title,
            action_type: actionType,
            note,
            contact_value: item.contact_label || "",
          }),
        });
        const payload = await safeJsonParse(response, {});
        if (!response.ok) {
          throw new Error(formatApiError(payload, "Failed to save contact action"));
        }
        setEntries((current) => [payload.entry || payload, ...current]);
        setContactState(payload.state || null);
        return payload.state || true;
      } catch (saveError) {
        setError(saveError.message || "Failed to save contact action");
        return false;
      } finally {
        setSavingAction("");
      }
    },
    [adminFetch, item]
  );

  const saveFollowUp = useCallback(
    async ({ followUpDate = "", followUpType = "", followUpNote = "", urgencyOverride = "" }) => {
      if (!item?.id) return false;

      setSavingFollowUp(true);
      setError("");
      try {
        const response = await adminFetch(`/admin/stakeholder/contacts/${encodeURIComponent(item.id)}/state`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            follow_up_date: followUpDate,
            follow_up_type: followUpType,
            follow_up_note: followUpNote,
            urgency_override: urgencyOverride,
          }),
        });
        const payload = await safeJsonParse(response, {});
        if (!response.ok) {
          throw new Error(formatApiError(payload, "Failed to save follow-up plan"));
        }
        setContactState(payload || null);
        return payload || true;
      } catch (saveError) {
        setError(saveError.message || "Failed to save follow-up plan");
        return false;
      } finally {
        setSavingFollowUp(false);
      }
    },
    [adminFetch, item?.id]
  );

  const editAction = useCallback(
    async ({ entryId, actionType, note = "" }) => {
      if (!item?.id || !entryId) return false;

      setSavingEntryId(entryId);
      setError("");
      try {
        const response = await adminFetch(
          `/admin/stakeholder/contacts/${encodeURIComponent(item.id)}/${encodeURIComponent(entryId)}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              action_type: actionType,
              note,
            }),
          }
        );
        const payload = await safeJsonParse(response, {});
        if (!response.ok) {
          throw new Error(formatApiError(payload, "Failed to update contact action"));
        }
        setEntries((current) =>
          current.map((entry) => (entry.entry_id === entryId ? payload.entry || entry : entry))
        );
        setContactState(payload.state || null);
        return payload;
      } catch (saveError) {
        setError(saveError.message || "Failed to update contact action");
        return false;
      } finally {
        setSavingEntryId("");
      }
    },
    [adminFetch, item?.id]
  );

  const deleteAction = useCallback(
    async ({ entryId }) => {
      if (!item?.id || !entryId) return false;

      setDeletingEntryId(entryId);
      setError("");
      try {
        const response = await adminFetch(
          `/admin/stakeholder/contacts/${encodeURIComponent(item.id)}/${encodeURIComponent(entryId)}`,
          {
            method: "DELETE",
          }
        );
        const payload = await safeJsonParse(response, {});
        if (!response.ok) {
          throw new Error(formatApiError(payload, "Failed to delete contact action"));
        }
        setEntries((current) => current.filter((entry) => entry.entry_id !== entryId));
        setContactState(payload.state || null);
        return payload;
      } catch (saveError) {
        setError(saveError.message || "Failed to delete contact action");
        return false;
      } finally {
        setDeletingEntryId("");
      }
    },
    [adminFetch, item?.id]
  );

  return {
    contactState,
    deleteAction,
    deletingEntryId,
    editAction,
    entries,
    error,
    loading,
    logAction,
    refresh: loadEntries,
    savingAction,
    savingEntryId,
    savingFollowUp,
    saveFollowUp,
  };
}
